import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { StyleSheet, Text, View } from './components/base_components';
import { useBusy, BusyOverlay } from './components/busy_component';
import BreadcrumbPath from './components/breadcrumb_path';
import DownloadProgress from './components/download_progress';
import ProgressSpinner from './components/progress_spinner';
import StandardBoxList from './components/standard_box_list';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';

import DownloadHelper from './lib/download_helper';
import VolumeDirHelper from './lib/volume_dir_helper';

import MediaGrantStore from './stores/media_grant_store';
import UserRequestStore from './stores/user_request_store';
import UserStore from './stores/user_store';

import { byteFormat } from './tools/util';
import { useLatestCallback } from './tools/latest_callback';

const styles = StyleSheet.create({
  downloadScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  actionButton: { marginLeft: '1rem' },
  spinner: { margin: '1.5rem', alignSelf: 'center' },
  text: {
    marginTop: '2rem',
    alignSelf: 'center',
    color: '#18182d',
    fontSize: '1.5rem',
    fontWeight: '400',
  },
  downloadSummary: {
    margin: '2rem',
    borderRadius: '1rem',
    alignSelf: 'stretch',
    backgroundColor: '#f9f9f9',
    borderWidth: 1,
    borderColor: '#ccc',
    flexDirection: 'column',
  },
  summaryLine: {
    paddingTop: '1.5rem',
    paddingBottom: '1.5rem',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    alignSelf: 'stretch',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#ddd',
  },
  first: { borderTopWidth: 0 },
  summaryLabel: { flex: 1, fontSize: '1.5rem', fontWeight: 'build' },
  summaryValue: {
    flex: 0.25,
    marginLeft: '1rem',
    marginRight: '1rem',
    fontSize: '1.5rem',
  },
  summaryButton: { alignSelf: 'flex-start' },
});

export default function DownloadScreen(props) {
  const params = useParams();
  const location = useLocation();
  const org = UserStore.useOrg();
  const grant_name = params.grant_name ?? '';
  const path = '/' + (params['*'] ?? '');
  const grant = MediaGrantStore.useMediaGrant(org?.org_id, grant_name);
  const media_grant_id = grant?.media_grant_id;
  const [list, setList] = React.useState<any[]>([]);
  const [is_ready, setIsReady] = React.useState(false);
  const [is_busy, setBusy, clearBusy] = useBusy();
  const [is_downloading, setIsDownloading] = React.useState(false);

  const _fetch = useLatestCallback((done?: () => void) => {
    if (location?.state && media_grant_id && org) {
      const { media_list, dir_list } = location.state;
      VolumeDirHelper.fetch(
        { org_id: org?.org_id, media_grant_id, path, media_list, dir_list },
        (err, found_list) => {
          if (err) {
            window.alert('Failed to collect files, please try again.');
          } else {
            setIsReady(true);
            setList(found_list);
          }
          done?.();
        }
      );
    } else {
      done?.();
    }
  });

  React.useEffect(() => {
    _fetch();
  }, [org, media_grant_id, path, location?.state, _fetch]);

  function _onDownloadPress() {
    if (list?.length > 0 && setBusy()) {
      setIsDownloading(true);
      _onRetry();
    }
  }
  function _onRequestPress() {
    const media_id_list = list
      .filter((m) => !m.in_s3 && m.file_size > 0)
      .map((m) => m.media_id);
    console.log('list:', list);
    console.log('media_id_list:', media_id_list);
    const yes = window.confirm(
      'Are you sure you want to request upload of these files?'
    );
    if (yes && media_id_list.length > 0 && setBusy()) {
      const opts = { org_id: org?.org_id, media_grant_id, media_id_list };
      UserRequestStore.createUserRequest(opts, (err) => {
        if (err) {
          window.alert('Failed to create user request, please try again.');
          clearBusy();
        } else {
          _fetch(() => {
            clearBusy();
          });
        }
      });
    }
  }
  function _onRetry() {
    const opts = { org_id: org?.org_id, media_grant_id, list, path };
    DownloadHelper.run(opts, (err) => {
      if (err === 'not_supported') {
        window.alert(
          'Looks like your browser does not suport multiple file downloads.'
        );
        setIsDownloading(false);
      } else if (err?.name === 'AbortError') {
        // user abort
        setIsDownloading(false);
      } else if (err) {
        window.alert('Failed to start download, please try again.');
        setIsDownloading(false);
      }
      clearBusy();
    });
  }
  let content: any = null;
  if (is_downloading) {
    content = (
      <DownloadProgress mediaGrant={grant} path={path} onRetry={_onRetry} />
    );
  } else if (is_ready) {
    let cloud_count = 0;
    let cloud_size = 0;
    let total_size = 0;
    let missing_count = 0;
    let missing_size = 0;
    let requested_count = 0;
    let requested_size = 0;
    const prefix_len = path.length;
    const rows = list.map((file) => {
      console.log('file:', file);
      total_size += file.file_size;
      const in_cloud = file.in_s3 || file.file_size === 0;
      let cloud = '';
      if (in_cloud) {
        cloud_count++;
        cloud_size += file.file_size;
        cloud = '✅';
      } else if (file.is_requested) {
        requested_count++;
        requested_size += file.file_size;
        cloud = 'Requested';
      } else {
        missing_count++;
        missing_size += file.file_size;
        cloud = '❌';
      }
      return [
        file.media_path.slice(prefix_len),
        byteFormat(file.file_size),
        cloud,
      ];
    });
    const buttons = (
      <>
        <TextButton
          style={styles.actionButton}
          disabled={cloud_count === 0}
          text='Download All'
          onPress={_onDownloadPress}
        />
      </>
    );

    content = (
      <>
        <TitleActionLine title='Download Files' />
        {missing_count > 0 || requested_count > 0 ? (
          <DownloadSummary
            totalCount={list.length}
            totalSize={total_size}
            requestedCount={requested_count}
            requestedSize={requested_size}
            missingCount={missing_count}
            missingSize={missing_size}
            onPress={_onRequestPress}
          />
        ) : null}
        <StandardBoxList
          title='List'
          detail={rows ? `${cloud_count} Files, ${byteFormat(cloud_size)}` : ''}
          buttons={buttons}
          headers={['Path', 'Size', 'In Cloud']}
          rows={rows}
          sizes={[3]}
        />
      </>
    );
  } else {
    content = (
      <>
        <TitleActionLine title='Download Files' />
        <Text style={styles.text}>Collecting directory entries...</Text>
        <ProgressSpinner style={styles.spinner} color='#999' />
      </>
    );
  }

  return location.state ? (
    <View style={styles.downloadScreen}>
      <BreadcrumbPath
        volumeName={grant?.grant_name}
        prefix={`/home/${grant?.grant_name}/`}
        path={path}
        extra='Download'
      />
      {content}
      <BusyOverlay isBusy={is_busy && !is_downloading} />
    </View>
  ) : (
    <Navigate to={`/home/${grant_name}${path}`} />
  );
}
interface DownloadSummaryProps {
  totalCount: number;
  totalSize: number;
  requestedCount: number;
  requestedSize: number;
  missingCount: number;
  missingSize: number;
  onPress: () => void;
}
function DownloadSummary(props: DownloadSummaryProps) {
  const cloud_count = props.totalCount - props.missingCount;
  const cloud_size = props.totalSize - props.missingSize;
  return (
    <View style={styles.downloadSummary}>
      <View style={[styles.summaryLine, styles.first]}>
        <Text style={styles.summaryLabel}>In Cloud</Text>
        <Text style={styles.summaryValue}>{cloud_count} Files</Text>
        <Text style={styles.summaryValue}>{byteFormat(cloud_size)}</Text>
      </View>
      <View style={styles.summaryLine}>
        <Text style={styles.summaryLabel}>Not In Cloud</Text>
        <Text style={styles.summaryValue}>{props.missingCount} Files</Text>
        <Text style={styles.summaryValue}>{byteFormat(props.missingSize)}</Text>
      </View>
      <View style={styles.summaryLine}>
        <Text style={styles.summaryLabel}>Not In Cloud - Upload Requested</Text>
        <Text style={styles.summaryValue}>{props.requestedCount} Files</Text>
        <Text style={styles.summaryValue}>
          {byteFormat(props.requestedSize)}
        </Text>
      </View>
      <View style={styles.summaryLine}>
        <Text style={styles.summaryLabel}>Total Files</Text>
        <Text style={styles.summaryValue}>{props.totalCount} Files</Text>
        <Text style={styles.summaryValue}>{byteFormat(props.totalSize)}</Text>
      </View>
      {props.missingCount > 0 ? (
        <View style={styles.summaryLine}>
          <TextButton
            style={styles.summaryButton}
            text='Request Cloud Upload'
            onPress={props.onPress}
          />
        </View>
      ) : null}
    </View>
  );
}
